import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { Security, LoginCallback } from "@okta/okta-react";
import LayOuts from "./components/LayOuts/LayOuts";
import Policies from "./components/Policies/Policies";
import PoliciesDetails from "./components/Policies/PoliciesDetails/PoliciesDetails";
import { useEffect, useState } from "react";
import Protected from "./components/Protected/Protected";

import { toRelativeUrl } from "@okta/okta-auth-js";
import Login from "./components/Login/Login";
import AppProvider from "./Context/AppContext";
import {
  OktaConfigProvider,
  useOktaConfig,
} from "./Context/CredentialsContext";
import Vendors from "./components/Vendors/Vendors";
import Questionnaires from "./components/Vendors/Questionnaires/Questionnaires";
import { Helmet, HelmetProvider } from "@dr.pogodin/react-helmet";
import VendorLogin from "./components/Login/VendorLogin";
import VendorLoginProvider from "./Context/vendorLogin";
import { Decryption } from "./lib/Decryption";
import ForgetPassword from "./components/Login/ForgetPassword";
import Otp from "./components/Login/Otp";
import DomainSwitchProvider from "./Context/DomainSwitch.Context";
import MarketingCompliance from "./components/Marketing Compliance/MarketingCompliance";
import MarketingDetails from "./components/Marketing Compliance/MarketingDetails";
import MarktingProvider from "./Context/MarktingContext";
import Setup from "./components/setup/Setup";
import SetupProvider from "./Context/SetupContext";
import Archived from "./components/Marketing Compliance/Archived";
import AdminPanelProvider from "./Context/AdminPanelContext";
import AdminPanel from "./components/Marketing Compliance/Admin/AdminPanel";
import Companies from "./components/Companies/Companies";
import Assignments from "./components/Assignments/Assignments";
import AssignmentsDetails from "./components/Assignments/Assignment Details/AssignmentsDetails";
import MainLoginLoading from './components/LoadingPages/MainLoginLoading';
import Complaints from './components/Complaints/Complaints/Complaints';
import ComplaintDetail from './components/Complaints/ComplaintDetail/ComplaintDetail';
import OnboardCustomer from './components/OnboardCustomer/OnboardCustomer';
import HQUsers from './components/HQUsers/HQUsers';
import RequireRole from './components/RequireRole/RequireRole';


const CALLBACK_PATH = "/login/callback";

function App() {
  const navigate = useNavigate();
  const { oktaAuths, setOktaConfig } = useOktaConfig();
  const location = useLocation();
  const [configTimedOut, setConfigTimedOut] = useState(false);

  useEffect(() => {
    if (oktaAuths) return;
    const timer = setTimeout(() => setConfigTimedOut(true), 10000);
    return () => clearTimeout(timer);
  }, [oktaAuths]);

  useEffect(() => {
    const credentials = localStorage.getItem("_Id");

    if (credentials) {
      const decryptData = Decryption(credentials);
      if (decryptData) setOktaConfig(decryptData);
    }
  }, [setOktaConfig]); // Ensure useEffect only depends on setOktaConfig

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin), {
      replace: true,
    });
  };
  if (
    location.pathname === "/vendorlogin" ||
    location.pathname === "/vendors" ||
    location.pathname === "/questions" ||
    location.pathname === "/forgetpassword" ||
    location.pathname === "/otp"
  ) {
    return (
      <HelmetProvider>
        <Helmet>
          <meta charSet="utf-8" />
          <title>Compliance Portal</title>
        </Helmet>
        <DomainSwitchProvider>
          <AppProvider>
            <VendorLoginProvider>
              <Routes>
                <Route element={<LayOuts />}>
                  <Route
                    path="/vendors"
                    element={
                      <Protected>
                        <Vendors />
                      </Protected>
                    }
                  />
                  <Route
                    path="/questions"
                    element={
                      <Protected>
                        <Questionnaires />
                      </Protected>
                    }
                  />
                  <Route path="/vendorlogin" element={<VendorLogin />} />
                  <Route
                    path="/forgetpassword"
                    element={<ForgetPassword />}
                  />
                  <Route path="/otp" element={<Otp />} />
                </Route>
              </Routes>
            </VendorLoginProvider>
          </AppProvider>
        </DomainSwitchProvider>
      </HelmetProvider>
    );
  }

  if (!oktaAuths) {
    return (
      <DomainSwitchProvider>
        {configTimedOut ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '12px' }}>
            <p style={{ color: '#C71D96', fontWeight: 600, fontSize: '1rem' }}>
              Unable to load portal configuration. Please contact your administrator.
            </p>
            <button onClick={() => window.location.reload()} className="btn btn-sm btn-outline-secondary">
              Retry
            </button>
          </div>
        ) : (
          <MainLoginLoading />
        )}
      </DomainSwitchProvider>
    );
  }

  return (
    <HelmetProvider>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Compliance Portal</title>
      </Helmet>
      <Security oktaAuth={oktaAuths} restoreOriginalUri={restoreOriginalUri}>
        <MarktingProvider>
          <AppProvider>
            <SetupProvider>
              <AdminPanelProvider>
                <Routes>
                  <Route element={<LayOuts />}>
                    <Route path="/" element={<Login />} />
                    <Route
                      path="/policies"
                      element={
                        <Protected>
                          <Policies />
                        </Protected>
                      }
                    />
                    <Route path={CALLBACK_PATH} element={<LoginCallback />} />
                    <Route
                      path="/details/:id"
                      element={
                        <Protected>
                          <PoliciesDetails />
                        </Protected>
                      }
                    />
                    <Route
                      path="/marketingcompliance"
                      element={
                        <Protected>
                          <MarketingCompliance />
                        </Protected>
                      }
                    />
                    <Route
                      path="/assignments"
                      element={
                        <Protected>
                          <Assignments />
                        </Protected>
                      }
                    />
                    <Route
                      path="/assignment/:assignmentId"
                      element={
                        <Protected>
                          <AssignmentsDetails />
                        </Protected>
                      }
                    />
                    <Route
                      path="/complaints"
                      element={
                        <Protected>
                          <Complaints />
                        </Protected>
                      }
                    />

                    {/* Detail page reads :id from the URL */}
                    <Route
                      path="/complaints/:id"
                      element={
                        <Protected>
                          <ComplaintDetail />
                        </Protected>} />
                    <Route
                      path="/companies"
                      element={
                        <Protected>
                          <Companies />
                        </Protected>
                      }
                    />
                    <Route
                      path="/marketing/details/:fileId"
                      element={
                        <Protected>
                          <MarketingDetails />
                        </Protected>
                      }
                    />
                    <Route
                      path="/setup"
                      element={
                        <Protected>
                          <Setup />
                        </Protected>
                      }
                    />
                    <Route
                      path="/setup/archived"
                      element={
                        <Protected>
                          <Archived />
                        </Protected>
                      }
                    />
                    <Route
                      path="/adminpanel"
                      element={
                        <Protected>
                          <AdminPanel />
                        </Protected>
                      }
                    />
                    <Route
                      path="/internal-users"
                      element={
                        <Protected>
                          <AdminPanel />
                        </Protected>
                      }
                    />
                    <Route
                      path="/admin/onboard-customer"
                      element={
                        <Protected>
                          <RequireRole allow="superAdmin">
                            <OnboardCustomer />
                          </RequireRole>
                        </Protected>
                      }
                    />
                    <Route
                      path="/admin/hq-users"
                      element={
                        <Protected>
                          <RequireRole allow="superAdmin">
                            <HQUsers />
                          </RequireRole>
                        </Protected>
                      }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </AdminPanelProvider>
            </SetupProvider>
          </AppProvider>
        </MarktingProvider>
      </Security>
    </HelmetProvider>
  );
}

export default function Root() {
  return (
    <OktaConfigProvider>
      <App />
    </OktaConfigProvider>
  );
}
